import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Settings from '../Settings';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import SchemaForm from '@afosto/app-components-react/SchemaForm';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import translations from './translations';

const ORDER_TAGS_PATH = 'afosto.tenant.odr.orders.tags';

const OrderTagsSettings = ({ pathID }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:orders:read']);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [orderTagsSettings, setOrderTagsSettings] = useState(null);
  const [formSchema, setFormSchema] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const hasReadPermissions = hasPermissions();

  const uiSchema = {
    tags: {
      props: {
        label: intl.formatMessage(translations.editDrawer.tagsArrayField.label),
        shouldRemoveId: false,
        prefillId: false,
      },
      code: {
        props: {
          label: intl.formatMessage(translations.editDrawer.codeField.label),
          helperText: intl.formatMessage(translations.editDrawer.codeField.helperText),
        },
      },
      color: {
        props: {
          type: 'color',
          label: intl.formatMessage(translations.editDrawer.colorField.label),
        },
      },
      label: {
        props: {
          label: intl.formatMessage(translations.editDrawer.labelField.label),
        },
      },
    },
  };

  const handleUpdateProfile = async fields => {
    try {
      const payload = fields?.tags ?? [];

      const { data: profileData = {} } = await profileApi.updateProfile(ORDER_TAGS_PATH, payload);
      setOrderTagsSettings({ tags: profileData });

      enqueueSnackbar(intl.formatMessage(translations.feedback.orderTagsSettingsUpdated), {
        variant: 'success',
      });
      setIsEditing(false);
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderTagsSettingsNotUpdated), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const handleFetchProfile = async () => {
      try {
        const { data: profileData = {} } = await profileApi.getProfile(ORDER_TAGS_PATH);

        if (isMounted) {
          setOrderTagsSettings({ tags: profileData });
        }
      } catch (error) {
        if (isMounted) {
          setOrderTagsSettings(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const fetchProfileFormSchema = async () => {
      try {
        const { data } = await profileApi.getProfileDefinitions(ORDER_TAGS_PATH);

        if (isMounted) {
          setFormSchema({
            type: 'object',
            properties: {
              tags: {
                type: data.type,
                items: data.items,
              },
            },
          });
        }
      } catch (error) {
        if (isMounted) {
          setFormSchema({});
        }
      }
    };

    if (hasReadPermissions) {
      fetchProfileFormSchema();
      handleFetchProfile();
    }

    return () => {
      isMounted = false;
    };
  }, [pathID, hasReadPermissions]);

  if (!hasReadPermissions) {
    return (
      <Settings title={intl.formatMessage(translations.title)} isLoading={false}>
        <UnauthorizedComponent />
      </Settings>
    );
  }

  return (
    <>
      <Settings title={intl.formatMessage(translations.title)} isLoading={isLoading}>
        <List dense disablePadding>
          <ListItem>
            <ListItemText
              primary={intl.formatMessage(translations.display.label)}
              secondary={
                orderTagsSettings?.tags
                  ? orderTagsSettings?.tags.map(tag => tag?.label).join(', ')
                  : '-'
              }
            />
            {hasPermissions(['odr:orders:write']) && (
              <Button onClick={() => setIsEditing(true)} variant="outlined" size="small">
                <FormattedMessage {...translations.actions.edit} />
              </Button>
            )}
          </ListItem>
        </List>
      </Settings>
      <FormDrawer
        isOpen={isEditing}
        title={intl.formatMessage(translations.editDrawer.title)}
        drawerWidth="420px"
        onClose={() => setIsEditing(false)}
      >
        {formSchema && (
          <SchemaForm
            schema={formSchema}
            uiSchema={uiSchema}
            values={orderTagsSettings}
            onSubmit={handleUpdateProfile}
            onCancel={() => setIsEditing(false)}
          />
        )}
      </FormDrawer>
    </>
  );
};

OrderTagsSettings.propTypes = {
  pathID: PropTypes.string,
};

OrderTagsSettings.defaultProps = {
  pathID: 'tenant',
};

export default OrderTagsSettings;
